import * as _ from 'lodash';
import * as qs from 'query-string';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { AxiosError } from 'axios';
import { IApplicationState, AppActionCreators, RecipientActionCreators, ICenterRecipientListResponse, IRecipientLookupResponse, IRecipientAliasDuplicateCheckResponse, IRecipientForSidebarResponse, ExpertSystemActionCreators, IRecipientSearchRequest } from '@nkr/store/index';
import { IBaseResponse, ICenter, IFwtSummary, IRecipientForList, IRecipientForSidebar, IRecipientSearchReportLookupData } from '@nkr/models/index';
import { HistogeneticsEntityType, MatchStatus, MenuLocation, PatientType, RecipientAction } from '@nkr/enums/index';
import { IRecipientProfileContext, RecipientProfileProvider } from '../../profile/models';
import { Button, Glyphicon } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Search from './search';
import Grid from './grid';
import RecipientNav from '../../recipient-nav';
import MediaQuery from 'react-responsive';
import View from './view';
import Drawer from 'rc-drawer';
import motionProps from '@nkr/components/motion';
import ActionButtonRenderer from '../../profile/action-button-renderer';
import CheekSwabRequest, { CheekSwabType } from '@nkr/views/cheek-swab-request-form';
import ConfirmDeleteModal from '../../confirm-delete-modal';
import FwlDetailsModal from '@nkr/views/target-new/modals/fwt-details-modal';
import { downloadFile } from '@nkr/services/download-file';

interface IProps {
    centerId: number,
    recipientId: number
}

interface IStateProps {
    center: ICenter,
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRecipientLookupData: (centerId: number, isAdmin: boolean) => Promise<IRecipientLookupResponse>,
    checkAlias: (recipientId: number, alias: string) => Promise<IRecipientAliasDuplicateCheckResponse>,
    getList: (request: IRecipientSearchRequest) => Promise<ICenterRecipientListResponse>,
    getRecipientForSidebar: (recipientId: number) => Promise<IRecipientForSidebarResponse>,
    updateFromSidebar: (centerId: number, recipientId: number, alias: string, firstName: string, lastName: string, email: string, mrn: string, isActive: boolean, inactiveReasons: number, isDeleted: boolean, sendComplianceEmails: boolean, gender: string, birthDate: string) => Promise<IBaseResponse>,
    deleteRecipient: (recipientId: number, reasonCode: number, reason: string, initials: string) => Promise<IBaseResponse>,
    printRequisition: (orderId: number, entityType: number) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IProps;

interface IState {
    list: IRecipientForList[],
    alias: string,
    firstName: string,
    lastName: string,
    email: string,
    statusList: string[],
    typeList: string[],
    voucherType: string,
    centerId: number,
    recipientId: number,
    lookupData: IRecipientSearchReportLookupData,
    view: {
        show: boolean,
        data: IRecipientForSidebar
    },
    cheekSwabModal: {
        show: boolean,
        type: CheekSwabType,
        recipientId: number
    },
    confirmDelete: {
        show: boolean,
        recipientId: number,
        recipientAlias: string
    },
    viewFwt: {
        show: boolean,
        fwtSummary: IFwtSummary
    }
}

class RecipientList extends Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        alias: '',
        firstName: '',
        lastName: '',
        email: '',
        statusList: ['active', 'inactive'],
        typeList: ['direct', 'paired', 'voucher', 'wlp'],
        voucherType: '',
        centerId: 0,
        recipientId: 0,
        lookupData: null,
        view: {
            show: false,
            data: null,
        },
        cheekSwabModal: {
            show: false,
            type: null,
            recipientId: 0
        },
        confirmDelete: {
            show: false,
            recipientId: 0,
            recipientAlias: ''
        },
        viewFwt: {
            show: false,
            fwtSummary: null
        },
    }

    container: HTMLDivElement;

    saveContainer = (container: HTMLDivElement) => {
        this.container = container;
    };

    getContainer = () => {
        return this.container;
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });

        let alias = parsed.alias != null ? parsed.alias : this.state.alias;
        let firstName = parsed.firstName != null ? parsed.firstName : this.state.firstName;
        let lastName = parsed.lastName != null ? parsed.lastName : this.state.lastName;
        let email = parsed.email != null ? parsed.email : this.state.email;
        let statusList = parsed.status != null ? parsed.status : this.state.statusList;
        let typeList = parsed.type != null ? parsed.type : this.state.typeList;
        let recipientId = parsed.rid != null ? parseInt(parsed.rid) : 0;
        let voucherType = parsed.voucherType != null ? parsed.voucherType : this.state.voucherType;

        let request: IRecipientSearchRequest = {
            centerId: this.props.centerId,
            statusList,
            typeList,
            voucherType,
            alias,
            firstName,
            lastName,
            email
        };

        this.props.showLoading('Getting recipients, one moment please...').then(() => {
            Promise.all([
                this.props.getList(request),
                this.props.getRecipientLookupData(this.props.centerId, this.props.groupIsSuper)
            ])
                .then(([listResponse, lookupResponse]) => {
                    if (listResponse.data.success && lookupResponse.data.success) {
                        this.setState({
                            list: listResponse.data.results,
                            alias,
                            statusList,
                            typeList,
                            recipientId,
                            lookupData: lookupResponse.data.results
                        }, () => {
                            this.setUrlParameters();

                            if (this.state.recipientId > 0) {
                                this.handleViewRecipient(this.state.recipientId);
                            }
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    refreshData = () => {
        let request: IRecipientSearchRequest = {
            centerId: this.props.centerId,
            statusList: this.state.statusList,
            typeList: this.state.typeList,
            voucherType: this.state.voucherType,
            alias: this.state.alias,
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            email: this.state.email
        };

        this.props.showLoading('Getting recipients, one moment please...').then(() => {
            this.props.getList(request)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            list: response.data.results,
                        }, () => {
                            this.setUrlParameters();

                            if (this.state.recipientId > 0) {
                                this.handleViewRecipient(this.state.recipientId);
                            }
                        });
                    }
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    setUrlParameters = () => {
        let { centerId, alias, firstName, lastName, email, statusList, typeList, voucherType, recipientId } = this.state;

        let paramList = [];

        if (centerId > 0) {
            paramList.push(encodeURI('center=' + centerId));
        }

        if (statusList.indexOf('active') >= 0) {
            paramList.push('status[]=active');
        }

        if (statusList.indexOf('inactive') >= 0) {
            paramList.push('status[]=inactive');
        }

        if (statusList.indexOf('pending') >= 0) {
            paramList.push('status[]=pending');
        }

        if (statusList.indexOf('transplanted') >= 0) {
            paramList.push('status[]=transplanted');
        }

        if (typeList.indexOf('direct') >= 0) {
            paramList.push('type[]=direct');
        }

        if (typeList.indexOf('paired') >= 0) {
            paramList.push('type[]=paired');
        }

        if (typeList.indexOf('voucher') >= 0) {
            paramList.push('type[]=voucher');
        }

        if (typeList.indexOf('wlp') >= 0) {
            paramList.push('type[]=wlp');
        }

        if (typeList.indexOf('krc') >= 0) {
            paramList.push('type[]=krc');
        }

        if (voucherType != '') {
            paramList.push('voucherType=' + voucherType);
        }

        if (alias != '') {
            paramList.push('alias=' + alias);
        }

        if (firstName != '') {
            paramList.push('firstName=' + firstName);
        }

        if (lastName != '') {
            paramList.push('lastName=' + lastName);
        }

        if (email != '') {
            paramList.push('email=' + email);
        }

        paramList.push('rid=' + recipientId);

        let params = paramList.join('&');

        let newUrl = '/center/' + this.props.centerId + '/recipients?' + params

        window.history.pushState({}, '', newUrl);
    }

    handleSearchFieldChange = (field: string, value: any) => {
        if (field == 'alias') {
            this.setState({ alias: value });
        }

        if (field == 'firstName') {
            this.setState({ firstName: value });
        }

        if (field == 'lastName') {
            this.setState({ lastName: value });
        }

        if (field == 'email') {
            this.setState({ email: value });
        }

        if (field == 'statusList') {
            this.setState({ statusList: value });
        }

        if (field == 'typeList') {
            this.setState({ typeList: value });
        }

        if (field == 'voucherType') {
            this.setState({ voucherType: value });
        }
    }

    handleSearch = () => {
        if (this.state.statusList.length == 0) {
            this.setState({
                statusList: ['active', 'inactive'],
            }, () => {
                this.setUrlParameters();
                this.getData();
            })
        } else {
            this.setUrlParameters();
            this.getData();
        }
    }

    handleViewRecipient = async (recipientId: number) => {
        let recipientResponse = await this.props.getRecipientForSidebar(recipientId);

        if (recipientResponse.data.success) {
            let data = recipientResponse.data.results;

            this.setState({
                view: {
                    show: true,
                    data
                },
                recipientId
            }, () => {
                this.setUrlParameters();
            });
        }
    }

    handleSave = () => {
        this.props.showLoading('Saving recipient data, one moment please...').then(() => {
            let { id, alias, firstName, lastName, email, mrn, isActive, inactiveReason, isDeleted, sendComplianceEmails, gender, birthDate } = this.state.view.data;

            this.props.updateFromSidebar(this.props.centerId, id, alias, firstName, lastName, email, mrn, isActive, inactiveReason, isDeleted, sendComplianceEmails, gender, birthDate)
                .then(response => {
                    if (response.data.success) {
                        this.refreshData();
                        toast.success('Recipient data saved successfully...');
                    } else {
                        var message = response.data?.message?.length > 0 ? response.data.message : "We were unable to update the recipient";
                        toast.error(message, { autoClose: 10000, closeOnClick: true });
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error(
                        <>
                            <div>Error occurred while saving the recipient data:</div>
                            <div>{error.message}</div>
                        </>
                    );
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    handleCancelView = () => {
        this.setState({
            view: {
                show: false,
                data: null
            },
            recipientId: 0
        }, () => {
            this.setUrlParameters();
        });
    }

    handleViewDataChange = (data: IRecipientForSidebar) => {
        this.setState({
            view: { ...this.state.view, data }
        });
    }

    handleCheekSwabRequest = (id: number, action: RecipientAction) => {
        let type = 0;

        if (action == RecipientAction.Order3gTissueTyping) {
            type = CheekSwabType.Order3gTissueTyping;
        }

        if (action == RecipientAction.UtilizeBulkCheekSwab) {
            type = CheekSwabType.UtilizeBulkCheekSwab;
        }

        this.setState({
            cheekSwabModal: {
                show: true,
                type,
                recipientId: id
            },
            recipientId: id
        });
    };

    handleConfirmDelete = (id: number) => {
        let data = this.state.list.find(x => x.id == id);

        this.setState({
            confirmDelete: {
                show: true,
                recipientId: data.id,
                recipientAlias: data.alias
            }
        });
    }

    handleDelete = (reasonCode: number, reason: string, initials: string) => {
        let recipientId = this.state.confirmDelete.recipientId;

        this.setState({
            confirmDelete: {
                show: false,
                recipientId: 0,
                recipientAlias: ''
            }
        }, () => {
            this.props.showLoading('Deleting recipient, one moment...').then(() => {
                this.props.deleteRecipient(recipientId, reasonCode, reason, initials)
                    .then(response => {
                        if (response.data.success) {
                            this.refreshData();
                        }
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        });
    }

    handleCancelConfirmDelete = () => {
        this.setState({
            confirmDelete: {
                show: false,
                recipientId: 0,
                recipientAlias: ''
            }
        });
    }

    handleItemSelected = (recipientId: number, action: RecipientAction) => {
        switch (action) {
            case RecipientAction.Order3gTissueTyping: this.handleCheekSwabRequest(recipientId, action); break;
            case RecipientAction.UtilizeBulkCheekSwab: this.handleCheekSwabRequest(recipientId, action); break;
            case RecipientAction.Delete: this.handleConfirmDelete(recipientId); break;
            case RecipientAction.ReprintRequisition: this.handleReprintRequisition(recipientId); break;
        }
    }

    handleFinishedCheekSwabModal = (cancel: boolean) => {
        let list = _.cloneDeep(this.state.list).map(x => {
            if (x.id == this.state.cheekSwabModal.recipientId) {
                if (!cancel) {
                    x.canOrderCheekSwab = false;
                }
            }

            return x;
        });

        this.setState({
            list,
            cheekSwabModal: {
                show: false,
                type: null,
                recipientId: null
            }
        });
    };

    handleReprintRequisition = (requisitionId: number) => {
        this.props.showLoading('Downloading requisition file, one moment please...').then(() => {
            this.props.printRequisition(requisitionId, HistogeneticsEntityType.Recipient).then((response: any) => {
                downloadFile(response.data, `requisition-file.pdf`, 'application/pdf');

                this.props.hideLoading();
            });
        });
    }

    handleShowFwt = (recipientId: number) => {
        let recipient = this.state.list.find(x => x.id == recipientId);

        let isHardToMatch = recipient?.isHardToMatch;
        let isVoucherHolder = recipient?.isVoucherHolder;
        let isPaired = recipient?.isPairedPatient;

        let fwtSummary: IFwtSummary = {
            recipientId,
            recipientAlias: recipient.alias,
            hardToMatch: isHardToMatch,
            isVoucherHolder,
            isPairedRecipient: isPaired,
            patientCpra: recipient?.cpra4 ?? 0,
            patientHistoricalDeclineRatio: recipient?.declineRatioHistoric ?? 0,
            donorsNeeded: recipient?.donorsNeeded ?? 0,
            aboCombo: recipient?.aboCombo ?? '',
            donorsCompatibleABO: recipient?.donorCompatibleAbo.toString() ?? '',
            aboAndAgeCompatibleDonors: recipient?.aboAndAgeCompatibleDonors.toString() ?? '',
            recipientBloodType: recipient?.bloodType ?? '',
            categories: recipient?.voucherCategories ?? [],
            fwt: recipient?.recipientFwtString ?? '',
            ewt: recipient?.estimatedWaitTime.toString() ?? '',
            awt: recipient?.actualWaitTime.toString() ?? '',           
            age: recipient?.age ?? 0 
        };

        this.setState({
            viewFwt: {
                show: true,
                fwtSummary
            }
        });
    };

    handleFwtClose = () => {
        this.setState({
            viewFwt: {
                show: false,
                fwtSummary: null
            }
        });
    };

    render = () => {
        const recipientId = this.state.view.data != null ? this.state.view.data.id : 0;
        const recipientCenterId = this.state.view.data != null ? this.state.view.data.centerId : 0;
        const hasMedicareAb = this.state.view.data != null ? this.state.view.data.hasMedicareAb : false;
        const centerHasRecipientShield = this.state.view.data != null ? this.state.view.data.centerHasRecipientShield : false;
        const recipientShieldEnrolled = this.state.view.data != null ? this.state.view.data.recipientShieldEnrolled : false;
        const pairStatus = this.state.view.data != null ? this.state.view.data.statusId : 0;
        const patientTypeId = this.state.view.data != null ? this.state.view.data.patientTypeId : 0;
        const voucherDonorId = this.state.view.data != null ? this.state.view.data.voucherDonorId : 0;
        const voucherDonorAlias = this.state.view.data != null ? this.state.view.data.voucherDonorAlias : '';
        const voucherDonorCenterId = this.state.view.data != null ? this.state.view.data.voucherDonorCenterId : 0;
        const canOrderCheekSwab = this.state.view.data != null ? this.state.view.data.canOrderCheekSwab : false;
        const canDelete = this.state.view.data != null ? this.state.view.data.canDelete : false;
        const isWaitlistEligible = this.state.view.data != null ? this.state.view.data.isWaitlistEligible : false;
        const orderId = this.state.view.data != null ? this.state.view.data.requisitionId : 0;

        let contextValue: IRecipientProfileContext = {
            activeKey: '',
            isAdmin: false,
            centerId: 0,
            recipientId,
            headerInfo: null,
            toggleType: 0,
            messages: [],
            showCheekOrderNotification: false,
            hasChanges: false,
            allowSaveWithNoChanges: false,
            dataChangedWarning: {
                show: false,
                title: '',
                href: ''
            },
            updateAfterLoad: () => null,
            updateErrors: () => null,
            updateHasChanges: () => null,
            updateDataChangedWarning: () => null,
            refreshHeader: () => null,
            onCheekSwabRequest: () => null,
            onCheekOrderNotify: () => null,
            registerSave: () => null,
            onItemSelected: this.handleItemSelected,
            updateAllowSaveWithNoChanges: () => null,
        };

        let pendingBioMatches = _.sumBy(this.state.list.filter(x => x.matchStatusId == MatchStatus.NeedsMatch && x.isExportable && x.patientTypeId != PatientType.Waitlist), x => x.pendingBioMatchDonors);

        return (
            <>
                <div ref={this.saveContainer} />

                <RecipientProfileProvider value={contextValue}>
                    <div style={{ padding: '0px 10px' }}>
                        <RecipientNav
                            activeKey={'RECIPIENT-LIST'}
                            isAdmin={false}
                            isNkrLogin={this.props.groupIsSuper}
                            centerId={this.props.centerId}
                            recipientId={this.props.recipientId}
                            recipientAlias={''}
                        />

                        <Search
                            alias={this.state.alias}
                            firstName={this.state.firstName}
                            lastName={this.state.lastName}
                            email={this.state.email}
                            transplanted={false}
                            statusList={this.state.statusList}
                            typeList={this.state.typeList}
                            voucherType={this.state.voucherType}
                            onFieldChange={this.handleSearchFieldChange}
                            onSearch={this.handleSearch}
                        />

                        <Grid
                            centerId={this.props.centerId}
                            data={this.state.list}
                            showPanel={this.state.view.show}
                            onShowFwt={this.handleShowFwt}
                            centerHasRecipientShield={this.props.center != null ? this.props.center.hasRecipientShield : false}
                            isAdminLogin={this.props.groupIsSuper}
                            onAction={this.handleItemSelected}
                            onViewRegistration={this.handleViewRecipient}
                        />
                        <div style={{ textAlign: 'right' }}>{pendingBioMatches} pending preselect(s)</div>
                    </div>

                    <MediaQuery minWidth={1023}>
                        <Drawer
                            width='34vw'
                            placement='right'
                            getContainer={this.getContainer}
                            open={this.state.view.show}
                            style={{ zIndex: 99999999 }}
                            {...motionProps}
                        >
                            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div style={{ padding: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                                        <h3 style={{ margin: 0 }}>Recipient Details</h3>
                                        <div>
                                            <ActionButtonRenderer
                                                recipientId={recipientId}
                                                centerId={this.props.centerId}
                                                orderId={orderId}
                                                recipientCenterId={recipientCenterId}
                                                hasMedicareAb={hasMedicareAb}
                                                centerHasRecipientShield={centerHasRecipientShield}
                                                recipientShieldEnrolled={recipientShieldEnrolled}
                                                statusId={pairStatus}
                                                isAdmin={false}
                                                isAdminLogin={this.props.groupIsSuper}
                                                menuLocation={MenuLocation.Sidebar}
                                                patientTypeId={patientTypeId}
                                                voucherDonorId={voucherDonorId}
                                                voucherDonorAlias={voucherDonorAlias}
                                                voucherDonorCenterId={voucherDonorCenterId}
                                                canOrderCheekSwab={canOrderCheekSwab}
                                                canDelete={canDelete}
                                                isWaitlistEligible={isWaitlistEligible}
                                                micrositeRecipientId={0}
                                                onMenuToggle={() => null}
                                            />
                                        </div>
                                        <div style={{ width: '33%', textAlign: 'right' }}>
                                            <Button bsStyle='link' bsSize='sm' onClick={this.handleCancelView}><Glyphicon glyph='remove' /></Button>
                                        </div>
                                    </div>

                                    <View
                                        centerId={this.state.centerId}
                                        isSuper={this.props.groupIsSuper}
                                        data={this.state.view.data}
                                        lookupData={this.state.lookupData}
                                        onSave={this.handleSave}
                                        onDataChange={this.handleViewDataChange}
                                    />
                                </div>

                                <div className='text-center bg-info'>
                                    <Button bsStyle='link' block onClick={this.handleCancelView}>Close This View</Button>
                                </div>
                            </div>
                        </Drawer>
                    </MediaQuery>
                </RecipientProfileProvider>

                <MediaQuery maxWidth={1023}>
                    <View
                        centerId={this.state.centerId}
                        isSuper={this.props.groupIsSuper}
                        data={this.state.view.data}
                        lookupData={this.state.lookupData}
                        onSave={this.handleSave}
                        onDataChange={this.handleViewDataChange}
                    />
                </MediaQuery>

                <CheekSwabRequest
                    centerId={this.props.centerId}
                    donorId={0}
                    recipientId={this.state.recipientId}
                    isModal={true}
                    show={this.state.cheekSwabModal.show}
                    type={this.state.cheekSwabModal.type}
                    onFinished={this.handleFinishedCheekSwabModal} />

                <ConfirmDeleteModal
                    show={this.state.confirmDelete.show}
                    alias={this.state.confirmDelete.recipientAlias}
                    reasonList={this.state.lookupData != null ? this.state.lookupData.deleteReasonList : []}
                    onSubmit={this.handleDelete}
                    onCancel={this.handleCancelConfirmDelete} />

                <FwlDetailsModal
                    show={this.state.viewFwt.show}
                    summaryData={this.state.viewFwt.fwtSummary}
                    onClose={this.handleFwtClose}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState, ownProps: IProps): IStateProps => {
    return {
        center: appState.app.center,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecipientLookupData: (centerId, isAdmin) => {
            return dispatch(RecipientActionCreators.getRecipientLookup(centerId, isAdmin));
        },
        checkAlias: (recipientId, alias) => {
            return dispatch(RecipientActionCreators.checkAlias(recipientId, alias))
        },
        getList: (request) => {
            return dispatch(RecipientActionCreators.getList(request, false));
        },
        getRecipientForSidebar: (recipientId) => {
            return dispatch(RecipientActionCreators.getRecipientForSidebar(recipientId));
        },
        updateFromSidebar: (centerId, recipientId, alias, firstName, lastName, email, mrn, isActive, inactiveReasons, isDeleted, sendComplianceEmails, gender, birthDate) => {
            return dispatch(RecipientActionCreators.updateFromSidebar(centerId, recipientId, alias, firstName, lastName, email, mrn, isActive, inactiveReasons, isDeleted, sendComplianceEmails, gender, birthDate));
        },
        deleteRecipient: (recipientId, reasonCode, reason, initials) => {
            return dispatch(RecipientActionCreators.deleteRecipient(recipientId, reasonCode, reason, initials));
        },
        printRequisition: (orderId, entityType) => {
            return dispatch(ExpertSystemActionCreators.printRequisition(orderId, entityType));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(RecipientList);

